<template>
  <section class="section-grid">

    <CampaignsAdsGridHeadline :columns="campaignsGridColumns" @ads-close="onAdClose()" @select-columns="onColumnsSelect" />

    <mq-layout class="grid-summary-m" :mq="['sm', 'md']">
      <div class="grid-summary-inner">
        <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ campaigns ? campaigns.Total : 0 }}</span>
      </div>
      <b-input :value="searchValue" :placeholder="$t('Grid.Search')" @blur="onSearchChange($event)" @keyup.native.enter="onSearchChange($event)"
            v-show="showSearchInput"></b-input>
      <b-button class="is-transparent grid-search-m" @click="showSearchInput = true">
        <icon-base width="17" height="17" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
      </b-button>
    </mq-layout>
    <CampaignsAdsGridContent
      :ads="ads"
      :columns="campaignsGridColumnsFiltered"
      @keywords-loaded="onKeywordsLoaded"
      @toggle-facebook-campaign-details="emitToggleFacebookCampaignDetails"
    />
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { DataSourceResult } from "@/models/DataSourceResult";
import { GridColumn } from "@/models/GridColumn";
import { TableSettings, TableSettingsStateColumn } from "@/models/TableSettings";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignAdGridViewModel } from "@/models/campaigns/CampaignAdGridViewModel";
import { CampaignKeywordGridViewModel } from "@/models/campaigns/CampaignKeywordGridViewModel";
import CampaignsAdsGridContent from "./CampaignsAdsGridContent.vue";
import CampaignsAdsGridHeadline from "./CampaignsAdsGridHeadline.vue";
import IconBase from "../IconBase.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconCaretRight from "../icons/IconCaretRight.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "campaignsModule";

@Component({
  components: {
    CampaignsAdsGridContent,
    CampaignsAdsGridHeadline,
    IconBase,
    IconSearch,
    IconCaretDown,
    IconCaretRight,
    IconCaretLeft,
  },
})
export default class CampaignsAdsGrid extends Vue {
  @Prop() readonly ads!: CampaignAdGridViewModel[] | null;
  
  //#region Vuex getters and actions

  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings[];
  @Action("setSearchValue", { namespace }) setSearchValue!: (searchValue: string) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  gridId = "ad_groups__ad";
  selectedColumnsIds: number[] = [];

  campaignsGridColumns: GridColumn[] = [
    {
      id: 1,
      name: "Actions",
      label: i18n.t("Campaigns.AdGroupsGrid.Columns.Actions").toString(),
      searchable: false,
      sortable: true,
      visible: true,
      forbidden: false,
    },
    { id: 2, name: "AdTitle", label: i18n.t("Campaigns.AdsGrid.Columns.AdTitle").toString(), class: "cell-ad-title", searchable: true, sortable: true, visible: true, forbidden: false, width: 300 },
    { id: 3, name: "Cost", label: i18n.t("Campaigns.AdGroupsGrid.Columns.Cost").toString(), class: "cell-ad-cost", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 4, name: "LeadCount", label: i18n.t("Campaigns.AdGroupsGrid.Columns.LeadCount").toString(), class: "cell-ad-lead-count", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 5, name: "AverageLeadCost", label: i18n.t("Campaigns.AdGroupsGrid.Columns.AverageLeadCost").toString(), class: "cell-ad-average-lead-cost", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 6, name: "AdStatusStr", label: i18n.t("Campaigns.AdsGrid.Columns.AdStatusStr").toString(), class: "cell-ad-status-str", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 7, name: "Conversion", label: i18n.t("Campaigns.AdGroupsGrid.Columns.Conversion").toString(), class: "cell-ad-conversion", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 8, name: "CTR", label: i18n.t("Campaigns.AdGroupsGrid.Columns.CTR").toString(), class: "cell-ad-ctr", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 9, name: "ClickCount", label: i18n.t("Campaigns.AdGroupsGrid.Columns.ClickCount").toString(), class: "cell-ad-click-count", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 10, name: "ViewCount", label: i18n.t("Campaigns.AdGroupsGrid.Columns.ViewCount").toString(), class: "cell-ad-view-count", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 11, name: "VisitorCount", label: i18n.t("Campaigns.AdGroupsGrid.Columns.VisitorCount").toString(), class: "cell-ad-visitor-count", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 12, name: "ImpressionCount", label: i18n.t("Campaigns.AdGroupsGrid.Columns.ImpressionCount").toString(), class: "cell-ad-impression-count", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 13, name: "Meetings", label: i18n.t("Campaigns.AdGroupsGrid.Columns.Meetings").toString(), class: "cell-ad-meetings", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 14, name: "UpdateTrackingTemplateStatusLocalized", label: i18n.t("Campaigns.AdsGrid.Columns.UpdateTrackingTemplateStatusLocalized").toString(), class: "cell-ad-tracking-template", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 15, name: "IsConnected", label: i18n.t("Campaigns.AdsGrid.Columns.IsConnected").toString(), class: "cell-ad-is-connected", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 16, name: "IsLeadsReceived", label: i18n.t("Campaigns.AdsGrid.Columns.IsLeadsReceived").toString(), class: "cell-ad-is-leads-received", searchable: true, sortable: true, visible: true, forbidden: false, width: 150 },
    { id: 17, name: "MeetingPrice", label: i18n.t("Campaigns.AdGroupsGrid.Columns.MeetingPrice").toString(), class: "cell-ad-meeting-price", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 18, name: "Deals", label: i18n.t("Campaigns.AdGroupsGrid.Columns.Deals").toString(), class: "cell-ad-deals", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 19, name: "DealAmount", label: i18n.t("Campaigns.AdGroupsGrid.Columns.DealAmount").toString(), class: "cell-ad-deal-amount", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 20, name: "AverageClickCost", label: i18n.t("Campaigns.AdGroupsGrid.Columns.AverageClickCost").toString(), class: "cell-ad-average-click-cost", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 21, name: "AverageViewCost", label: i18n.t("Campaigns.AdGroupsGrid.Columns.AverageViewCost").toString(), class: "cell-ad-average-view-cost", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 22, name: "AverageVisitorCost", label: i18n.t("Campaigns.AdGroupsGrid.Columns.AverageVisitorCost").toString(), class: "cell-ad-average-visitor-cost", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
    { id: 23, name: "AverageImpressionCost", label: i18n.t("Campaigns.AdGroupsGrid.Columns.AverageImpressionCost").toString(), class: "cell-ad-average-Impression-cost", searchable: true, sortable: true, visible: false, forbidden: false, width: 150 },
  ];

  showSearchInput = false;

  //#endregion Properties


  //#region Watchers

  @Watch("tableSettings", { immediate: true })
  onTableSettingsChange(newValue: TableSettings[], oldValue: TableSettings[]): void {
    if (newValue === undefined) {
      return;
    } else {
      let settings: TableSettings | undefined = newValue.find((i: TableSettings) => i.GridId === this.gridId);
      if (settings) {
        this.updateColumns(settings);
      }
    }
  }

  @Watch("campaignsPageData", { immediate: true })
  onCampaignsPageDataChange(newValue: CampaignsPageViewModel, oldValue: CampaignsPageViewModel): void {
    if (oldValue === undefined || newValue === null) {
      return;
    } else {
      let settings: TableSettings | undefined = this.tableSettings.find((i: TableSettings) => i.GridId === this.gridId);
      if (settings) {
        this.updateColumns(settings);
      }
    }
  }

  //#endregion Watchers


  //#region Getters

  get campaignsGridColumnsFiltered(): GridColumn[] {
    // if no selected columns (like on first page load), select all columns
    if (this.selectedColumnsIds.length === 0) {
      for (let i = 0; i < this.campaignsGridColumns.length; i++) {
        if (this.campaignsGridColumns[i].visible && !this.campaignsGridColumns[i].forbidden) {
          this.selectedColumnsIds.push(this.campaignsGridColumns[i].id);
        }
      }
    }
    for (let i = 0; i < this.campaignsGridColumns.length; i++) {
      this.campaignsGridColumns[i].visible = this.selectedColumnsIds.indexOf(this.campaignsGridColumns[i].id) >= 0;
    }
    return this.campaignsGridColumns;
  }

  //#endregion Getters
  

  //#region Handlers

  emitToggleFacebookCampaignDetails(ad: CampaignAdGridViewModel): void {
    this.$emit("toggle-facebook-campaign-details", ad);
  }

  onAdClose() {
    this.$emit("ads-close");
  }

  onColumnsSelect(selectedColumnsIds: number[]) {
    this.selectedColumnsIds = selectedColumnsIds;
  }

  onKeywordsLoaded(keywords: CampaignKeywordGridViewModel[], ad: CampaignAdGridViewModel) {
    this.$emit("keywords-loaded", keywords, ad);
  }

  onSearchChange(e: Event) {
    this.setSearchValue((e.target as HTMLInputElement).value);
  }

  //#endregion Handlers


  //#region Private methods

  private updateColumns(tableSettings: TableSettings) {
    for (let i = 0; i < this.campaignsGridColumns.length; i++) {
      const tableSettingsColumn: TableSettingsStateColumn | undefined = tableSettings?.StateObject.columns.find((column: TableSettingsStateColumn) => {
        let columnName: string | undefined = column.field;
        if (!columnName) {
          columnName = column.title;
        }
        switch (columnName) {
          case "Actions":
          case "פעולות":
            columnName = "Actions"
            break;
        }
        return columnName === this.campaignsGridColumns[i].name;
      });

      this.campaignsGridColumns[i].visible = !tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden;
    }
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-grid {
  background: #fff;
  border: 1px solid #d3d6e2;
  border-radius: 4px;
  margin-bottom: 30px;
}
.grid-bottom {
  padding: 9px 15px 11px;
  .grid-bottom-inner {
    display: flex;
    justify-content: space-between;
  }
  .per-page {
    .per-page-label {
      font-weight: 600;
    }
    .per-page-dropdown {
      .button-text-w {
        margin-left: 7px;
      }
      a.dropdown-item,
      .dropdown .dropdown-menu .has-link a,
      .dropdown .dropdown-menu .has-link a,
      button.dropdown-item {
        padding: 3px 5px;
      }
    }
  }
  .grid-show {
    display: flex;
    .show-text {
      margin-left: 5px;
    }
    .show-nav {
      display: flex;
      align-items: center;
      .show-nav-button {
        padding: 0;
        width: 30px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:hover {
          color: $link;
        }
        &.show-nav-button-prev {
        }
        &.show-nav-button-next {
        }
      }
    }
  }
}
.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;
  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}
.grid-summary-m {
  height: 49px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #435c65;
  .button.grid-search-m {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 49px;
  }
  .grid-summary-inner {
    padding: 10px 15px;
    .summary-label {
      font-weight: 600;
    }
  }
}

html[dir="ltr"] {
  .grid-bottom {
    .per-page {
      .per-page-dropdown {
        .button-text-w {
          margin: 0 7px 0 0;
        }
      }
    }
    .grid-show {
      .show-text {
        margin: 0 5px 0 0;
      }
      .show-nav {
        .show-nav-button {
          transform: scaleX(-1);
        }
      }
    }
  }
}
</style>
